import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Package, BarChart3, ShoppingCart, Warehouse, Bell, LogOut,
  Plus, Eye, TrendingUp, Users, Download, RefreshCw, Box,
  Truck, FileText, Activity, DollarSign, Archive, UserPlus, Settings, Shield, Smartphone, Mail
} from 'lucide-react';

// Sub-components
import DashboardTab   from '../components/wms/tabs/DashboardTab';
import AITab          from '../components/wms/tabs/AITab';
import AgentTab       from '../components/wms/tabs/AgentTab';
import ProductsTab    from '../components/wms/tabs/ProductsTab';
import OrdersTab      from '../components/wms/tabs/OrdersTab';
import InventoryTab   from '../components/wms/tabs/InventoryTab';
import ReceivingTab   from '../components/wms/tabs/ReceivingTab';
import SuppliersTab   from '../components/wms/tabs/SuppliersTab';
import CustomersTab   from '../components/wms/tabs/CustomersTab';
import ReportsTab     from '../components/wms/tabs/ReportsTab';
import UsersTab       from '../components/wms/tabs/UsersTab';
import AuditTab       from '../components/wms/tabs/AuditTab';
import ActivityTab    from '../components/wms/tabs/ActivityTab';
import ShopeeTab       from '../components/wms/tabs/ShopeeTab';
import SettingsTab    from '../components/wms/tabs/SettingsTab';
import ImportTab      from '../components/wms/tabs/ImportTab';
import SecurityTab    from '../components/wms/tabs/SecurityTab';
import CrudModal          from '../components/wms/modals/CrudModal';
import LossModal          from '../components/wms/modals/LossModal';
import ReconciliationModal from '../components/wms/modals/ReconciliationModal';

import ConfirmDialog      from '../components/wms/modals/ConfirmDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';

const TABS = [
  { id: 'dashboard', icon: BarChart3,   label: 'Dashboard' },
  { id: 'ai',        icon: Activity,    label: 'AI Intelligence' },
  { id: 'agent',     icon: Eye,         label: 'Agora Brain' },
  { id: 'products',  icon: Package,     label: 'Products' },
  { id: 'orders',    icon: ShoppingCart,label: 'Orders' },
  { id: 'inventory', icon: Warehouse,   label: 'Inventory' },
  { id: 'suppliers', icon: Truck,       label: 'Suppliers' },
  { id: 'customers', icon: Users,       label: 'Customers' },
  { id: 'reports',   icon: FileText,    label: 'Reports' },
  { id: 'users',     icon: UserPlus,    label: 'Users' },
  { id: 'audit',     icon: FileText,    label: 'Audit & Compliance' },
  { id: 'settings',  icon: Settings,    label: 'Settings' },
  { id: 'security',  icon: Shield,      label: 'Security' },
  { id: 'import',    icon: Download,    label: 'Import & Connect' },
];

export default function WMSDashboard() {
  const nav = useNavigate();
  const [tab, setTab]         = useState(() => localStorage.getItem('wms_tab') || 'dashboard');
  const switchTab = (t)       => { setTab(t); localStorage.setItem('wms_tab', t); };

  // Core data
  const [stats, setStats]             = useState({});
  const [products, setProducts]       = useState([]);
  const [orders, setOrders]           = useState([]);
  const [warehouses, setWarehouses]   = useState([]);
  const [customers, setCustomers]     = useState([]);
  const [users, setUsers]             = useState([]);
  const [alerts, setAlerts]           = useState([]);
  const [activity, setActivity]       = useState([]);
  const [suppliers, setSuppliers]     = useState([]);
  const [purchases, setPurchases]     = useState([]);
  const [lots, setLots]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [toast, setToast]             = useState(null);
  const showToast = (msg, type = 'error') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };
  useEffect(() => { window.__wmsToast = showToast; return () => { delete window.__wmsToast; }; }, []);

  // Sync-history anomaly state (drives the red dot next to Import & Connect in the sidebar)
  const [syncAnomaly, setSyncAnomaly] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchAnomaly = () => {
      axios.get(`${API}/connectors/sync-history/stats?platform=shopify&days=30`)
        .then(r => { if (!cancelled) setSyncAnomaly(r.data?.anomaly || null); })
        .catch(() => {});
    };
    fetchAnomaly();
    const id = setInterval(fetchAnomaly, 5 * 60 * 1000);   // poll every 5 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Form state
  const [modal, setModal]             = useState(null);
  const [editItem, setEditItem]       = useState(null);
  const [form, setForm]               = useState({});
  const [orderItems, setOrderItems]   = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [search, setSearch]           = useState('');

  // Financial
  const [costingMethod, setCostingMethod] = useState('average');
  const [profitReport, setProfitReport]   = useState(null);

  // AI / Agent
  const [forecasts, setForecasts]         = useState([]);
  const [reorderAlerts, setReorderAlerts] = useState([]);
  const [chatMessages, setChatMessages]   = useState([]);
  const [chatInput, setChatInput]         = useState('');
  const [aiLoading, setAiLoading]         = useState({});
  const [aiEngine, setAiEngine]           = useState('gemini');
  const [ollamaModel, setOllamaModel]     = useState('llama3.2:3b');
  const [aiCacheLoaded, setAiCacheLoaded] = useState(false);
  const [agentData, setAgentData]         = useState({ stats: {}, drafts: [], actions: [], insights: '' });
  const [agentLoading, setAgentLoading]   = useState({});
  const [notifSettings, setNotifSettings] = useState({ notification_email_enabled: '', notification_email_to: '' });

  // Audit
  const [auditData, setAuditData]   = useState({ log: [], ledger: [], reconciliation: null, valuation: null, approvals: [], stats: {} });
  const [auditTab, setAuditTab]     = useState(() => localStorage.getItem('wms_audit_tab') || 'overview');
  const switchAuditTab = (t)        => { setAuditTab(t); localStorage.setItem('wms_audit_tab', t); };
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [reconModal, setReconModal]   = useState(false);
  const [reconForm, setReconForm]     = useState({ reason: '', category: 'unidentified' });

  // Loss
  const [lossModal, setLossModal]     = useState(null);
  const [lossForm, setLossForm]       = useState({ quantity: 1, category: 'theft', reason: '', evidence: '' });

  // Confirm
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [wmsConfig, setWmsConfig] = useState({
    currency_symbol: 'S$', currency_code: 'SGD',
    tax_rates: [{ id: 'none', name: 'No Tax', rate: 0 }, { id: 'gst9', name: 'GST 9% (SG)', rate: 9 }]
  });
  const [exchangeRates, setExchangeRates] = useState({ base: 'USD', rates: {}, source: '' });

  const user = JSON.parse(localStorage.getItem('wms_user') || '{}');

  const loadWmsConfig = async () => {
    try {
      const [cfgRes, fxRes] = await Promise.all([
        axios.get(`${API}/settings/config`),
        axios.get(`${API}/settings/exchange-rates`),
      ]);
      setWmsConfig(cfgRes.data);
      setExchangeRates(fxRes.data);
    } catch {}
  };

  // Auth guard
  useEffect(() => { if (!localStorage.getItem('wms_token')) nav('/wms/login'); }, [nav]);

  // AI cache persistence
  const saveAiCache = async (type, data) => {
    try { await axios.put(`${API}/ai/cache/${type}`, { data }); } catch {}
  };
  useEffect(() => {
    const loadAiCache = async () => {
      try {
        const [chatRes, forecastRes, reorderRes] = await Promise.all([
          axios.get(`${API}/ai/cache/chat`),
          axios.get(`${API}/ai/cache/forecasts`),
          axios.get(`${API}/ai/cache/reorder`),
        ]);
        if (chatRes.data.data?.length) setChatMessages(chatRes.data.data);
        if (forecastRes.data.data?.length) setForecasts(forecastRes.data.data);
        if (reorderRes.data.data?.length) setReorderAlerts(reorderRes.data.data);
      } catch {}
      setAiCacheLoaded(true);
    };
    loadAiCache();
    axios.get(`${API}/ai/settings`).then(r => {
      setAiEngine(r.data.engine || 'gemini');
      setOllamaModel(r.data.ollama_model || 'llama3:8b');
    }).catch(() => {});
  }, []);
  useEffect(() => { if (aiCacheLoaded && chatMessages.length > 0) saveAiCache('chat', chatMessages); }, [chatMessages, aiCacheLoaded]);
  useEffect(() => { if (aiCacheLoaded && forecasts.length > 0) saveAiCache('forecasts', forecasts); }, [forecasts, aiCacheLoaded]);
  useEffect(() => { if (aiCacheLoaded && reorderAlerts.length > 0) saveAiCache('reorder', reorderAlerts); }, [reorderAlerts, aiCacheLoaded]);

  // Main data load
  const load = useCallback(async () => {
    try {
      const [s, p, o, w, c, u, al, act, pu, sup, lots_res, costing_res, profit_res] = await Promise.all([
        axios.get(`${API}/dashboard/stats`), axios.get(`${API}/products`),
        axios.get(`${API}/orders`),          axios.get(`${API}/warehouses`),
        axios.get(`${API}/customers`),       axios.get(`${API}/users`),
        axios.get(`${API}/inventory/alerts`),axios.get(`${API}/activity?limit=30`),
        axios.get(`${API}/purchases`),       axios.get(`${API}/suppliers`),
        axios.get(`${API}/lots`),            axios.get(`${API}/settings/costing`),
        axios.get(`${API}/reports/profit`),
      ]);
      setStats(s.data); setProducts(Array.isArray(p.data) ? p.data : []);
      setOrders(Array.isArray(o.data) ? o.data : []); setWarehouses(Array.isArray(w.data) ? w.data : []);
      setCustomers(Array.isArray(c.data) ? c.data : []); setUsers(Array.isArray(u.data) ? u.data : []);
      setAlerts(Array.isArray(al.data) ? al.data : []); setActivity(Array.isArray(act.data) ? act.data : []);
      setPurchases(Array.isArray(pu.data) ? pu.data : []); setSuppliers(Array.isArray(sup.data) ? sup.data : []);
      setLots(Array.isArray(lots_res.data) ? lots_res.data : []);
      setCostingMethod(costing_res.data.costing_method);
      setProfitReport(profit_res.data);
    } catch (e) { if (e.response?.status === 401) { localStorage.removeItem('wms_token'); nav('/wms/login'); } }
    setLoading(false);
  }, [nav]);
  useEffect(() => { load(); loadWmsConfig(); }, [load]);

  // Audit load
  const loadAudit = async () => {
    try {
      const [log, ledger, recon, val, appr, st] = await Promise.all([
        axios.get(`${API}/audit/log?limit=50`), axios.get(`${API}/audit/stock-ledger?limit=50`),
        axios.get(`${API}/audit/reconciliation`), axios.get(`${API}/audit/inventory-valuation`),
        axios.get(`${API}/audit/approvals`),      axios.get(`${API}/audit/stats`),
      ]);
      setAuditData({
        log: Array.isArray(log.data) ? log.data : [], ledger: Array.isArray(ledger.data) ? ledger.data : [],
        reconciliation: recon.data, valuation: val.data,
        approvals: Array.isArray(appr.data) ? appr.data : [], stats: st.data || {}
      });
      setAuditLoaded(true);
    } catch {}
  };
  useEffect(() => { if (tab === 'audit' && !auditLoaded) loadAudit(); }, [tab, auditLoaded]);

  // Agent load
  const loadAgent = async () => {
    try {
      const [st, dr, ac, ns] = await Promise.all([
        axios.get(`${API}/agent/stats`), axios.get(`${API}/agent/draft-pos`),
        axios.get(`${API}/agent/actions?limit=30`), axios.get(`${API}/agent/notifications/settings`),
      ]);
      setAgentData(p => ({ ...p, stats: st.data || {}, drafts: Array.isArray(dr.data) ? dr.data : [], actions: Array.isArray(ac.data) ? ac.data : [] }));
      setNotifSettings(ns.data || {});
    } catch {}
  };
  useEffect(() => { if (tab === 'agent') loadAgent(); }, [tab]);

  // CRUD helpers
  const close = () => { setModal(null); setEditItem(null); setForm({}); setOrderItems([]); };
  const F = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const saveProduct = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0, cost: parseFloat(form.cost) || 0, stock: parseInt(form.stock) || 0, min_stock: parseInt(form.min_stock) || 5, weight: parseFloat(form.weight) || 0 };
    if (editItem) await axios.put(`${API}/products/${editItem.product_id}`, payload);
    else await axios.post(`${API}/products`, payload);
    close(); load();
  };
  const delProduct = (id) => setConfirmDialog({ message: 'Archive this product? It will be hidden but preserved for audit.', onConfirm: async () => { try { await axios.delete(`${API}/products/${id}`); load(); } catch(e) { showToast(e.response?.data?.detail || 'Cannot archive'); } setConfirmDialog(null); } });
  const saveOrder = async () => {
    const shipping  = parseFloat(form.shipping_fee) || 0;
    const discount  = parseFloat(form.discount) || 0;
    const gstRate   = parseFloat(form.gst_rate) || 0;
    const subtotal  = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
    const beforeTax = subtotal + shipping - discount;
    const gstAmt    = parseFloat((beforeTax * gstRate / 100).toFixed(2));
    const grandTotal = parseFloat((beforeTax + gstAmt).toFixed(2));
    await axios.post(`${API}/orders`, {
      ...form,
      items:        orderItems,
      shipping_fee: shipping,
      discount:     discount,
      gst_rate:     gstRate,
      gst_amount:   gstAmt,
      grand_total:  grandTotal,
    });
    close(); load();
  };
  const orderStatus = async (id, status) => {
    setOrders(prev => prev.map(o => o.order_id === id ? { ...o, status } : o));
    try { await axios.put(`${API}/orders/${id}/status`, { status }); } catch { }
    load();
  };
  const saveWarehouse = async () => {
    const payload = { ...form, capacity: parseInt(form.capacity) || 1000 };
    if (editItem) await axios.put(`${API}/warehouses/${editItem.warehouse_id}`, payload);
    else await axios.post(`${API}/warehouses`, payload);
    close(); load();
  };
  const delWarehouse = (id) => setConfirmDialog({ message: 'Delete this warehouse?', onConfirm: async () => { try { await axios.delete(`${API}/warehouses/${id}`); load(); } catch(e) { showToast(e.response?.data?.detail || 'Cannot delete'); } setConfirmDialog(null); } });
  const saveCustomer = async () => {
    if (editItem) await axios.put(`${API}/customers/${editItem.customer_id}`, form);
    else await axios.post(`${API}/customers`, form);
    close(); load();
  };
  const saveUser = async () => {
    if (editItem) await axios.put(`${API}/users/${editItem.user_id}`, form);
    else await axios.post(`${API}/users`, form);
    close(); load();
  };
  const delUser = (id) => setConfirmDialog({ message: 'Delete this user?', onConfirm: async () => { await axios.delete(`${API}/users/${id}`); load(); setConfirmDialog(null); } });
  const saveSupplier = async () => {
    try {
      const payload = { ...form, lead_time_days: parseInt(form.lead_time_days) || 7 };
      if (editItem) await axios.put(`${API}/suppliers/${editItem.supplier_id}`, payload);
      else await axios.post(`${API}/suppliers`, payload);
      close(); load();
    } catch(e) { showToast(e.response?.data?.detail || 'Error saving supplier'); }
  };
  const delSupplier = (id) => setConfirmDialog({ message: 'Delete this supplier?', onConfirm: async () => { await axios.delete(`${API}/suppliers/${id}`); load(); setConfirmDialog(null); } });
  const savePurchase = async () => {
    const fc        = form.invoice_currency || wmsConfig.currency_code || 'SGD';
    const fxRate    = parseFloat(form.fx_rate) || 1;
    const isForeign = fc !== (wmsConfig.currency_code || 'SGD');
    const subtotalBase = isForeign
      ? purchaseItems.reduce((s, i) => s + i.unit_cost * i.quantity * fxRate, 0)
      : purchaseItems.reduce((s, i) => s + (i.unit_cost * i.quantity), 0);
    const freight   = parseFloat(form.freight_cost) || 0;
    const discount  = parseFloat(form.supplier_discount) || 0;
    const gstRate   = parseFloat(form.gst_rate) || 0;
    const beforeGst = subtotalBase + freight - discount;
    const gstAmt    = parseFloat((beforeGst * gstRate / 100).toFixed(2));
    const totalCost = parseFloat((beforeGst + gstAmt).toFixed(2));
    await axios.post(`${API}/purchases`, {
      ...form,
      items:             purchaseItems,
      invoice_currency:  fc,
      fx_rate:           fxRate,
      freight_cost:      freight,
      supplier_discount: discount,
      gst_rate:          gstRate,
      gst_amount:        gstAmt,
      total_cost:        totalCost,
    });
    close(); setPurchaseItems([]); load();
  };

  const changeCostingMethod = async (method) => {
    await axios.put(`${API}/settings/costing/${method}`);
    setCostingMethod(method);
    try {
      const [profitRes, lotRes] = await Promise.all([axios.get(`${API}/reports/profit`), axios.get(`${API}/lots`)]);
      setProfitReport(profitRes.data); setLots(Array.isArray(lotRes.data) ? lotRes.data : []);
    } catch {}
  };

  // AI helpers
  const switchAiEngine    = async (engine) => { await axios.put(`${API}/ai/settings/engine/${engine}`); setAiEngine(engine); };
  const switchOllamaModel = async (model)  => { await axios.put(`${API}/ai/settings/ollama-model/${model}`); setOllamaModel(model); };
  const loadForecast      = async () => {
    setAiLoading(p => ({ ...p, forecast: true }));
    try { const res = await axios.get(`${API}/ai/forecast`); setForecasts(res.data.forecasts || []); } catch {}
    setAiLoading(p => ({ ...p, forecast: false }));
  };
  const loadReorderAlerts = async () => {
    setAiLoading(p => ({ ...p, reorder: true }));
    try { const res = await axios.get(`${API}/ai/reorder-alerts`); setReorderAlerts(res.data.alerts || []); } catch {}
    setAiLoading(p => ({ ...p, reorder: false }));
  };
  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setAiLoading(p => ({ ...p, chat: true }));
    try {
      const res = await axios.post(`${API}/ai/chat`, { message: msg });
      setChatMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch (e) {
      const errMsg = e.response?.data?.detail || (e.code === 'ECONNABORTED' ? 'Request timed out. Try a simpler question or switch to Gemini.' : 'AI unavailable');
      setChatMessages(prev => [...prev, { role: 'ai', text: errMsg }]);
    }
    setAiLoading(p => ({ ...p, chat: false }));
  };

  // Agent helpers
  const runAgentScan = async () => {
    setAgentLoading(p => ({ ...p, scan: true }));
    try { await axios.post(`${API}/agent/scan`); await loadAgent(); }
    catch (e) { showToast(e.response?.data?.detail || 'Scan failed'); }
    setAgentLoading(p => ({ ...p, scan: false }));
  };
  const loadInsights = async () => {
    setAgentLoading(p => ({ ...p, insights: true }));
    try { const r = await axios.get(`${API}/agent/insights`); setAgentData(p => ({ ...p, insights: r.data.insights || '' })); } catch {}
    setAgentLoading(p => ({ ...p, insights: false }));
  };
  const [poActionLoading, setPoActionLoading] = useState(null);
  const poActionRef = React.useRef(new Set());
  const [justApprovedId, setJustApprovedId] = useState(null);
  const approveDraftPO = async (draftId, qty, cost) => {
    if (poActionLoading || poActionRef.current.has(draftId)) return;
    poActionRef.current.add(draftId);
    setPoActionLoading(draftId);
    try { await axios.put(`${API}/agent/draft-pos/${draftId}/approve`, { approved_by: user.email, quantity: qty, unit_cost: cost }, { timeout: 15000 }); setJustApprovedId(draftId); setTimeout(() => setJustApprovedId(null), 6000); } catch {}
    try { await loadAgent(); } catch {}
    poActionRef.current.delete(draftId);
    setPoActionLoading(null);
  };
  const rejectDraftPO = async (draftId, notes) => {
    if (poActionLoading || poActionRef.current.has(draftId)) return;
    poActionRef.current.add(draftId);
    setPoActionLoading(draftId);
    try { await axios.put(`${API}/agent/draft-pos/${draftId}/reject`, { approved_by: user.email, notes }, { timeout: 15000 }); } catch {}
    try { await loadAgent(); } catch {}
    poActionRef.current.delete(draftId);
    setPoActionLoading(null);
  };

  // Audit helpers
  const submitReconciliation = async () => {
    try {
      await axios.post(`${API}/audit/reconciliation/request`, { reason: reconForm.reason, category: reconForm.category, requested_by: user.email || 'demo@agora-sg' });
      setReconModal(false); setReconForm({ reason: '', category: 'unidentified' });
      loadAudit(); switchAuditTab('approvals');
    } catch (e) { showToast(e.response?.data?.detail || 'Error submitting reconciliation'); }
  };
  const submitLossReport = async () => {
    try {
      await axios.post(`${API}/audit/loss/report`, { product_id: lossModal.product_id, quantity: parseInt(lossForm.quantity) || 1, category: lossForm.category, reason: lossForm.reason, evidence: lossForm.evidence, requested_by: user.email || 'demo@agora-sg' });
      setLossModal(null); setLossForm({ quantity: 1, category: 'theft', reason: '', evidence: '' }); load();
    } catch (e) { showToast(e.response?.data?.detail || 'Error reporting loss'); }
  };

  const filteredProducts = products.filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase()));
  const orderTotal    = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const purchaseTotal = purchaseItems.reduce((s, i) => s + (i.unit_cost * i.quantity), 0);
  const cur           = wmsConfig.currency_symbol || 'S$';
  const tz            = wmsConfig.timezone        || 'Asia/Singapore';

  // Notification panel
  const [notifOpen, setNotifOpen] = useState(false);
  const unreadAgent   = agentData.actions.filter(a => !a.acknowledged).slice(0, 5);
  const totalNotifs   = alerts.length + unreadAgent.length;
  const markAllAgent  = async () => { await axios.put(`${API}/agent/actions/acknowledge-all`); loadAgent(); };

  // Badge dismissal — remember the count at the time the user last opened the panel.
  // Shows badge only for *new* notifications received after that moment.
  const [seenCount, setSeenCount] = useState(() => {
    const v = parseInt(localStorage.getItem('wms_notif_seen_count') || '0', 10);
    return isNaN(v) ? 0 : v;
  });
  const unseenCount = Math.max(0, totalNotifs - seenCount);
  // If items disappear (resolved), keep the baseline honest — but wait until
  // data has actually loaded so the baseline isn't reset to 0 on page refresh.
  useEffect(() => {
    if (loading) return;
    if (seenCount > totalNotifs) {
      setSeenCount(totalNotifs);
      localStorage.setItem('wms_notif_seen_count', String(totalNotifs));
    }
  }, [totalNotifs, seenCount, loading]);
  const handleBellClick = () => {
    setNotifOpen(v => {
      const next = !v;
      if (next) {
        // Opening — mark current total as seen
        setSeenCount(totalNotifs);
        localStorage.setItem('wms_notif_seen_count', String(totalNotifs));
      }
      return next;
    });
  };

  // 2FA suggestion banner — shown once after login if 2FA not set up
  const [show2faBanner, setShow2faBanner] = useState(
    () => !localStorage.getItem('wms_2fa_banner_dismissed') && user.role === 'admin'
  );
  const dismiss2faBanner = () => {
    setShow2faBanner(false);
    localStorage.setItem('wms_2fa_banner_dismissed', '1');
  };

  // Close notification panel on outside click
  React.useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
    if (!e.target.closest('[data-notif-panel="true"]')) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center"><Package className="w-5 h-5 text-white" /></div>
            <div><span className="font-bold text-lg text-gray-900">Agora WMS</span><span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">v2.1</span></div>
          </div>
          <div className="flex items-center gap-4">
            {/* Notification bell */}
            <div className="relative" data-notif-panel="true">
              <button onClick={handleBellClick}
                className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="notifications-bell">
                <Bell className="w-5 h-5" />
                {unseenCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold"
                    data-testid="notifications-badge">
                    {unseenCount > 9 ? '9+' : unseenCount}
                  </span>
                )}
              </button>

              {/* Dropdown panel */}
              {notifOpen && (
                <div className="absolute right-0 top-10 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                    <p className="font-semibold text-sm text-gray-800">Notifications</p>
                    <div className="flex gap-2">
                      {unreadAgent.length > 0 && (
                        <button onClick={() => { markAllAgent(); }} className="text-[10px] text-gray-400 hover:text-gray-600">Mark all read</button>
                      )}
                      <button onClick={() => setNotifOpen(false)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {totalNotifs === 0 ? (
                      <div className="p-6 text-center text-gray-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">All caught up!</p>
                      </div>
                    ) : (
                      <>
                        {/* Stock alerts */}
                        {alerts.length > 0 && (
                          <>
                            <div className="px-4 py-2 bg-amber-50 border-b">
                              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Stock Alerts ({alerts.length})</p>
                            </div>
                            {alerts.slice(0, 4).map((p, i) => (
                              <button key={i} onClick={() => { switchTab('inventory'); setNotifOpen(false); }}
                                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-amber-50 border-b border-gray-50 transition-colors text-left">
                                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${p.stock === 0 ? 'bg-red-500' : 'bg-amber-500'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                                  <p className="text-xs text-gray-500">{p.stock === 0 ? 'Out of stock' : `Low stock: ${p.stock} left`} · {p.sku}</p>
                                </div>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold shrink-0 ${p.stock === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {p.stock === 0 ? 'OUT' : 'LOW'}
                                </span>
                              </button>
                            ))}
                            {alerts.length > 4 && (
                              <button onClick={() => { switchTab('inventory'); setNotifOpen(false); }}
                                className="w-full text-xs text-amber-600 hover:text-amber-800 py-2 border-b text-center">
                                +{alerts.length - 4} more stock alerts →
                              </button>
                            )}
                          </>
                        )}

                        {/* Agent alerts */}
                        {unreadAgent.length > 0 && (
                          <>
                            <div className="px-4 py-2 bg-purple-50 border-b">
                              <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Agora Brain ({unreadAgent.length} unread)</p>
                            </div>
                            {unreadAgent.map((a, i) => (
                              <button key={i} onClick={() => { switchTab('agent'); setNotifOpen(false); }}
                                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-purple-50 border-b border-gray-50 transition-colors text-left">
                                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.severity === 'critical' ? 'bg-red-500' : a.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-gray-700 leading-snug line-clamp-2">{a.details}</p>
                                  <p className="text-[10px] text-gray-400 mt-0.5">{a.timestamp?.split('.')[0]?.replace('T', ' ')}</p>
                                </div>
                              </button>
                            ))}
                            <button onClick={() => { switchTab('agent'); setNotifOpen(false); }}
                              className="w-full text-xs text-purple-600 hover:text-purple-800 py-2 text-center">
                              View all in Agora Brain →
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">{(user.name || 'A')[0]}</div>
              <div><p className="text-sm font-medium text-gray-700">{user.name}</p><p className="text-[10px] text-gray-400 uppercase">{user.role}</p></div>
            </div>
            <button onClick={() => { localStorage.removeItem('wms_token'); localStorage.removeItem('wms_user'); nav('/wms/login'); }} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>


      {/* 2FA setup suggestion banner — shows once for admin users without 2FA */}
      {show2faBanner && (
        <div className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Smartphone className="w-4 h-4 shrink-0" />
            <span className="text-sm">
              <strong>Secure your account:</strong> Enable Google Authenticator (2FA) for an extra layer of protection.
              Go to <button onClick={() => { switchTab('security'); dismiss2faBanner(); }} className="underline font-semibold hover:text-blue-200">Security</button> → "Enable 2FA".
            </span>
          </div>
          <button onClick={dismiss2faBanner} className="text-blue-200 hover:text-white text-lg leading-none shrink-0">×</button>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-48 bg-white border-r min-h-[calc(100vh-56px)] p-3 hidden md:flex flex-col justify-between shrink-0">
          <div>
            <nav className="space-y-0.5">
              {TABS.map(t => (
                <button key={t.id} onClick={() => switchTab(t.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all whitespace-nowrap ${
                    tab === t.id
                      ? t.id === 'shopee' ? 'bg-orange-50 text-orange-700 shadow-sm'
                        : t.id === 'security' ? 'bg-red-50 text-red-700 shadow-sm'
                        : 'bg-blue-50 text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}>
                  <t.icon className={`w-4 h-4 shrink-0 ${
                    t.id === 'shopee' && tab !== 'shopee' ? 'text-orange-400'
                    : t.id === 'security' && tab !== 'security' ? 'text-red-400'
                    : ''
                  }`} />
                  <span className="flex-1 text-left">{t.label}</span>
                  {t.id === 'import' && syncAnomaly?.active && (
                    <span
                      title={`Stock conflict anomaly — today: ${syncAnomaly.today_conflicts}, 7-day avg: ${syncAnomaly.avg_7day}`}
                      className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0"
                      data-testid="sidebar-anomaly-dot" />
                  )}
                </button>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button onClick={() => window.open(`${API}/export/products`, '_blank')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-gray-500 hover:bg-gray-50 whitespace-nowrap"><Download className="w-4 h-4 shrink-0" />Export Products</button>
              <button onClick={() => window.open(`${API}/export/orders`, '_blank')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-gray-500 hover:bg-gray-50 whitespace-nowrap"><Download className="w-4 h-4 shrink-0" />Export Orders</button>
            </div>
          </div>
          <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <p className="text-xs font-semibold text-blue-800">Shopee Connected</p>
            <p className="text-[10px] text-blue-600 mt-0.5">Last sync: 2 min ago</p>
            <div className="flex items-center gap-1.5 mt-2"><div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div><span className="text-[10px] text-emerald-700 font-medium">API Active</span></div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 max-w-[1400px]">
          {loading && (
            <div className="flex items-center justify-center h-80">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-100 rounded-full"></div>
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                  <div className="w-16 h-16 flex items-center justify-center absolute top-0 left-0"><Package className="w-6 h-6 text-blue-500" /></div>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Loading warehouse data</p>
                  <p className="text-xs text-gray-400 mt-1">Connecting to all systems...</p>
                </div>
              </div>
            </div>
          )}

          {!loading && <>
            {tab === 'dashboard' && <DashboardTab stats={stats} orders={orders} alerts={alerts} load={load} switchTab={switchTab} cur={cur} />}
            {tab === 'ai'        && <AITab aiEngine={aiEngine} ollamaModel={ollamaModel} chatMessages={chatMessages} chatInput={chatInput} aiLoading={aiLoading} forecasts={forecasts} reorderAlerts={reorderAlerts} setChatInput={setChatInput} sendChat={sendChat} loadForecast={loadForecast} loadReorderAlerts={loadReorderAlerts} switchAiEngine={switchAiEngine} switchOllamaModel={switchOllamaModel} />}
            {tab === 'agent'     && <AgentTab API={API} agentData={agentData} agentLoading={agentLoading} notifSettings={notifSettings} setNotifSettings={setNotifSettings} runAgentScan={runAgentScan} loadInsights={loadInsights} approveDraftPO={approveDraftPO} rejectDraftPO={rejectDraftPO} loadAgent={loadAgent} user={user} cur={cur} poActionLoading={poActionLoading} justApprovedId={justApprovedId} />}
            {tab === 'inventory' && <InventoryTab warehouses={warehouses} alerts={alerts} setEditItem={setEditItem} setForm={setForm} setModal={setModal} delWarehouse={delWarehouse} cur={cur} purchases={purchases} products={products} setPurchaseItems={setPurchaseItems} API={API} switchTab={switchTab} />}
            {tab === 'products'  && <ProductsTab products={products} filteredProducts={filteredProducts} search={search} setSearch={setSearch} setEditItem={setEditItem} setForm={setForm} setModal={setModal} setLossModal={setLossModal} setLossForm={setLossForm} delProduct={delProduct} cur={cur} />}
            {tab === 'orders'    && <OrdersTab orders={orders} load={load} setEditItem={setEditItem} setForm={setForm} setOrderItems={setOrderItems} setModal={setModal} orderStatus={orderStatus} cur={cur} tz={tz} />}
            {tab === 'suppliers' && <SuppliersTab suppliers={suppliers} setEditItem={setEditItem} setForm={setForm} setModal={setModal} delSupplier={delSupplier} />}
            {tab === 'customers' && <CustomersTab customers={customers} setEditItem={setEditItem} setForm={setForm} setModal={setModal} />}
            {tab === 'reports'   && <ReportsTab profitReport={profitReport} products={products} lots={lots} costingMethod={costingMethod} changeCostingMethod={changeCostingMethod} cur={cur} />}
            {tab === 'users'     && <UsersTab users={users} setEditItem={setEditItem} setForm={setForm} setModal={setModal} delUser={delUser} />}
            {tab === 'audit'     && <AuditTab API={API} auditData={auditData} auditTab={auditTab} switchAuditTab={switchAuditTab} loadAudit={loadAudit} setReconModal={setReconModal} submitReconciliation={submitReconciliation} user={user} activity={activity} loadActivity={load} />}
            {tab === 'settings'  && <SettingsTab onConfigChange={loadWmsConfig} user={user} />}
            {tab === 'security'  && <SecurityTab user={user} />}
            {tab === 'import'    && <ImportTab products={products} tz={tz} />}
          </>}

          {/* Support footer — persistent across all tabs */}
          <footer className="mt-10 pt-6 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500" data-testid="wms-support-footer">
            <span>Agora WMS &middot; {new Date().getFullYear()}</span>
            <a href="mailto:support@agora-sg.com"
              className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium"
              data-testid="wms-support-email">
              <Mail className="w-3.5 h-3.5" />support@agora-sg.com
            </a>
          </footer>
        </main>
      </div>

      {/* Modals */}
      {modal && (
        <CrudModal
          modal={modal} editItem={editItem} form={form} F={F} setForm={setForm} close={close}
          warehouses={warehouses} products={products} customers={customers} suppliers={suppliers}
          orderItems={orderItems} setOrderItems={setOrderItems}
          purchaseItems={purchaseItems} setPurchaseItems={setPurchaseItems}
          orderTotal={orderTotal} purchaseTotal={purchaseTotal}
          saveProduct={saveProduct} saveOrder={saveOrder} saveWarehouse={saveWarehouse}
          saveCustomer={saveCustomer} saveUser={saveUser} saveSupplier={saveSupplier} savePurchase={savePurchase}
          wmsConfig={wmsConfig}
          exchangeRates={exchangeRates}
        />
      )}
      <LossModal lossModal={lossModal} lossForm={lossForm} setLossModal={setLossModal} setLossForm={setLossForm} submitLossReport={submitLossReport} />
      <ReconciliationModal reconModal={reconModal} setReconModal={setReconModal} reconForm={reconForm} setReconForm={setReconForm} submitReconciliation={submitReconciliation} auditData={auditData} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[100] max-w-sm px-5 py-3 rounded-xl shadow-2xl border backdrop-blur-sm animate-[slideUp_0.3s_ease-out]
          ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{toast.msg}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-current opacity-50 hover:opacity-100">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
